import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Image from '@base/Image';
import Paper from '@base/Paper/Paper';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getIsEnrolled } from '@selectors/combats';
import useWindowSize from '@hooks/useWindowSize';
import { pluralize } from '@utils/textHelper';
import {
  getTime,
  subtractTime,
  addTime,
  getFullDateWithDayAtStart
} from '@utils/dates';
import andConjunction from '@utils/andConjunction';
import { breakpoints, Spacings, Fonts } from '@styles/index';
import Actions from './Actions';
import Timer from './Timer';

const TimeImage = 'combat/time.svg';
const CubeImage = 'combat/cube.svg';
const QuestionMarkImage = 'combat/question-mark.svg';

const Wrapper = styled(Paper)`
  display: flex;
  grid-column: span 18;
  padding: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_6B};
  border-top: ${({ $borderColor }) => `5px solid  ${$borderColor}`};

  @media only screen and (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const Header = styled.div`
  min-width: 400px;
  width: 400px;

  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    min-width: 100%;
  }
`;

const Info = styled.div`
  border-left: 1px solid var(--color-divider);
  margin-left: ${Spacings.SPACING_7B};
  padding-left: ${Spacings.SPACING_7B};
  align-self: center;

  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    border: none;
    border-top: 1px solid var(--color-divider);
    padding: 0px;
    margin: 0px;
    margin-top: ${Spacings.SPACING_12B};
    padding-top: ${Spacings.SPACING_12B};
  }
`;

const CombatDate = styled(P1)`
  margin-top: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
`;

const Item = styled.div`
  margin-top: ${Spacings.SPACING_4B};
  display: grid;
  align-items: center;
  grid-template: 1fr / max-content 1fr;
  grid-column-gap: ${Spacings.SPACING_4B};

  &:first-of-type {
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const Description = styled(P1)`
  color: var(--color-text-secondary);
`;

const CombatCard = ({ data }) => {
  const { uid, name } = data;
  const duration_for_join_cta =
    data.duration_for_join_cta || data.durationForJoinCta;
  const starts_at = data.starts_at || data.startsAt;
  const topic_groups = data.topic_groups || data.topicGroups;
  const is_enrolled = data.is_enrolled || data.isEnrolled;
  const is_live = data.is_live || data.isLive;
  const quiz_details = data.quiz_details || data.quizDetails;
  const isEnrolled = useSelectorWithShallowEqual(getIsEnrolled(uid));
  const { isMobile } = useWindowSize();

  const duration = quiz_details?.duration || 0;
  const now = new Date();
  const startTime = new Date(starts_at);
  const endTime = addTime(startTime, duration, 'm');
  const preStartTime = subtractTime(startTime, duration_for_join_cta);

  const [isLive, setIsLive] = useState(() => {
    if (is_live) {
      return true;
    }

    return (
      preStartTime.getTime() <= now.getTime() &&
      now.getTime() <= endTime.getTime()
    );
  });

  const handleOnLive = useCallback(() => {
    setIsLive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnCompleted = useCallback(() => {
    setIsLive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEnrolledInCombat = isEnrolled || is_enrolled;
  const isToday = preStartTime.toDateString() === now.toDateString();
  const isInFuture = now.getTime() <= preStartTime.getTime();
  const isPast = endTime.getTime() <= now.getTime();

  const getCardColor = () => {
    if (isPast) {
      return 'transparent';
    }

    if (isLive && isEnrolledInCombat) {
      return 'var(--color-i-green)';
    }

    if (isEnrolledInCombat) {
      return 'var(--color-i-blue)';
    }

    return 'transparent';
  };

  const getTopics = () => {
    const items = [];

    topic_groups?.forEach?.((item) => {
      items.push(item.name);
    });

    return items;
  };

  const getCombatInfo = () => {
    const topics = getTopics();

    const items = [
      {
        image: TimeImage,
        label: `${duration} mins`
      },
      {
        image: CubeImage,
        label: andConjunction(topics)
      },
      {
        image: QuestionMarkImage,
        label: `${pluralize(quiz_details?.section || 0, 'round')} • ${pluralize(
          quiz_details?.questions || 0,
          'question'
        )}`
      }
    ];

    return (
      <Info>
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Item key={index}>
            <Image
              height={24}
              width={24}
              src={item.image}
              alt={`item-image-${index}`}
            />
            <Description color="var(--color-text-secondary)">
              {item.label}
            </Description>
          </Item>
        ))}
      </Info>
    );
  };

  const renderAction = () => (
    <Actions isPast={isPast} isLive={isLive} data={data} />
  );

  const color = getCardColor();

  return (
    <>
      <Wrapper $borderColor={color}>
        <Header>
          <H4 lineClamp={1}>{name}</H4>
          <CombatDate>
            {`${getFullDateWithDayAtStart(starts_at, false)} • ${getTime(
              starts_at
            )}`}
          </CombatDate>
          {!isMobile && renderAction()}
        </Header>
        {getCombatInfo()}
        {isMobile && renderAction()}
      </Wrapper>
      {isToday && isInFuture && (
        <Timer timestamp={preStartTime.getTime()} onExpire={handleOnLive} />
      )}
      {isToday && isLive && (
        <Timer timestamp={endTime.getTime()} onExpire={handleOnCompleted} />
      )}
    </>
  );
};

CombatCard.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired
};

export default CombatCard;
